/**
 * Behavior tracking — single API for all user behavior events.
 * Routes: GA4 (page/time/scroll), Mixpanel (detailed actions), Sentry (breadcrumbs).
 * Deduplicates events and keeps payloads small for performance.
 */

import { getTrackingContext } from './trackingContextStore'
import { trackEvent as trackEventUtils, trackPageView as ga4PageView } from '@/utils/analytics'
import { addBreadcrumb } from '@/config/sentry'

// ─── Metadata ───────────────────────────────────────────────────────────────

export type BehaviorMetadata = {
  user_id: string | null
  org_id: string | null
  role: string | null
  feature_name: string | null
  page_id: string | null
  page_name: string | null
}

export function getBehaviorMetadata(overrides?: Partial<BehaviorMetadata>): BehaviorMetadata {
  const ctx = getTrackingContext()
  return {
    user_id: overrides?.user_id ?? ctx.user_id,
    org_id: overrides?.org_id ?? ctx.org_id,
    role: overrides?.role ?? ctx.role,
    feature_name: overrides?.feature_name ?? ctx.feature_name,
    page_id: overrides?.page_id ?? ctx.page_id,
    page_name: overrides?.page_name ?? ctx.page_name,
    ...overrides,
  }
}

function mergePayload(extra: Record<string, unknown> = {}): Record<string, unknown> {
  return { ...getBehaviorMetadata(), ...extra }
}

// ─── Deduplication ──────────────────────────────────────────────────────────

const DEDUPE_MS = 500
let lastEventKey: string | null = null
let lastEventTime = 0

function dedupe(key: string): boolean {
  const now = Date.now()
  if (key === lastEventKey && now - lastEventTime < DEDUPE_MS) return true
  lastEventKey = key
  lastEventTime = now
  return false
}

// Scroll depth: fire once per level per page
let scrollDepthSent: Record<string, Set<number>> = {}

function scrollDepthDedupe(pageKey: string, depth: number): boolean {
  if (!scrollDepthSent[pageKey]) scrollDepthSent[pageKey] = new Set()
  if (scrollDepthSent[pageKey].has(depth)) return true
  scrollDepthSent[pageKey].add(depth)
  return false
}

export function resetScrollDepthForPage(pageKey: string): void {
  delete scrollDepthSent[pageKey]
}

// ─── Page & route ────────────────────────────────────────────────────────────

/**
 * Page opened — clear "which screen is open". GA4 + Mixpanel + Sentry.
 * Use page_id for filtering (e.g. "orders", "order-details"); page_name for display ("Orders List", "Order Details #123").
 */
export function trackPageView(
  pageName: string,
  pagePath: string,
  options?: { page_id?: string; extra?: Record<string, unknown> }
): void {
  const page_id = options?.page_id ?? (pagePath.replace(/^\//, '').split('/')[0] || 'home')
  const fullUrl =
    typeof window !== 'undefined' && window.location ? window.location.href : pagePath
  const payload = mergePayload({
    page_id,
    page_name: pageName,
    page_path: pagePath,
    pathname: pagePath,
    full_url: fullUrl,
    event_action: 'page_opened',
    event_label: `Page Opened: ${pageName}`,
    ...options?.extra,
  })

  ga4PageView(pageName, pagePath)
  trackEventUtils('Page Opened', payload, {
    mixpanelEventName: `Page Opened — ${pageName}`,
  })
  trackEventUtils(
    'page_viewed',
    {
      page: pagePath,
      pathname: pagePath,
      full_url: fullUrl,
      page_name: pageName,
      page_id,
      title: typeof document !== 'undefined' ? document.title : undefined,
      ...options?.extra,
    },
    { mixpanelEventName: `Page Viewed — ${pageName}` }
  )
  addBreadcrumb({
    category: 'navigation',
    message: `Page Opened: ${pageName}`,
    level: 'info',
    data: { page_id, page_name: pageName, page_path: pagePath },
  })
}

/**
 * Route change — "navigated from X to Y". Includes the new page as context.
 */
export function trackRouteChange(
  fromPath: string,
  toPath: string,
  pageName: string,
  options?: { page_id?: string; extra?: Record<string, unknown> }
): void {
  const page_id = options?.page_id ?? (toPath.replace(/^\//, '').split('/')[0] || 'home')
  const payload = mergePayload({
    page_id,
    page_name: pageName,
    page_path: toPath,
    from_path: fromPath,
    to_path: toPath,
    event_action: 'route_change',
    event_label: `Navigated to: ${pageName}`,
    ...options?.extra,
  })

  trackEventUtils('Route Change', payload, {
    mixpanelEventName: `Route Change — ${pageName}`,
  })
  addBreadcrumb({
    category: 'navigation',
    message: `Navigated to: ${pageName}`,
    level: 'info',
    data: { from_path: fromPath, to_path: toPath, page_id, page_name: pageName },
  })
}

/**
 * Time spent on screen — GA4 (engagement_time) + Mixpanel + Sentry.
 * Clearly tagged with page_id and page_name so you see "Time on Orders List: 45s".
 */
export function trackTimeSpent(
  pagePath: string,
  pageName: string,
  seconds: number,
  extra?: Record<string, unknown>
): void {
  const page_id = pagePath.replace(/^\//, '').split('/')[0] || 'home'
  const payload = mergePayload({
    page_id,
    page_name: pageName,
    page_path: pagePath,
    time_seconds: seconds,
    engagement_time_msec: seconds * 1000,
    event_action: 'time_on_page',
    event_label: `Time on ${pageName}: ${Math.round(seconds)}s`,
    ...extra,
  })

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'user_engagement', {
      engagement_time_msec: Math.round(seconds * 1000),
      page_path: pagePath,
      page_title: pageName,
    })
  }
  trackEventUtils('time_on_page', payload, {
    mixpanelEventName: `Time on Page — ${pageName}`,
  })
  addBreadcrumb({
    category: 'engagement',
    message: `Time on ${pageName}: ${Math.round(seconds)}s`,
    level: 'info',
    data: { page_path: pagePath, time_seconds: seconds },
  })
}

// ─── Clicks ──────────────────────────────────────────────────────────────────

/**
 * Click — "Action: Click" with page context so you see "Clicked X on page Y".
 */
export function trackClick(
  elementKey: string,
  label?: string,
  extra?: Record<string, unknown>
): void {
  const key = `click:${elementKey}`
  if (dedupe(key)) return

  const actionLabel = label ?? elementKey
  const payload = mergePayload({
    action_type: 'click',
    action_name: actionLabel,
    element: elementKey,
    label: actionLabel,
    event_action: 'click',
    event_label: `Click: ${actionLabel}`,
    ...extra,
  })

  trackEventUtils('Action: Click', payload)
  addBreadcrumb({
    category: 'ui',
    message: `Click: ${actionLabel}${payload.page_name ? ` (on ${payload.page_name})` : ''}`,
    level: 'info',
    data: { element: elementKey, page_id: payload.page_id, page_name: payload.page_name },
  })
}

// ─── Scroll ──────────────────────────────────────────────────────────────────

/**
 * Scroll depth — "Action: Scroll" with page context. Once per depth (25/50/75/100) per page.
 */
export function trackScrollDepth(
  pageKey: string,
  depthPercent: number,
  extra?: Record<string, unknown>
): void {
  if (scrollDepthDedupe(pageKey, depthPercent)) return

  const payload = mergePayload({
    action_type: 'scroll',
    action_name: `Scrolled to ${depthPercent}%`,
    scroll_depth_percent: depthPercent,
    page_key: pageKey,
    event_action: 'scroll',
    event_label: `Scroll: ${depthPercent}%`,
    ...extra,
  })

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'scroll', {
      percent_scrolled: depthPercent,
      page_path: window.location.pathname,
    })
  }
  trackEventUtils('Action: Scroll', payload)
  addBreadcrumb({
    category: 'engagement',
    message: `Scroll: ${depthPercent}%${payload.page_name ? ` (on ${payload.page_name})` : ''}`,
    level: 'info',
    data: { page_key: pageKey, depth_percent: depthPercent, page_name: payload.page_name },
  })
}

// ─── Form ────────────────────────────────────────────────────────────────────

/**
 * Form interaction — "Action: Form" with page context. Focus (first per form) and submit.
 */
export function trackFormInteraction(
  formId: string,
  action: 'focus' | 'submit' | 'blur',
  extra?: Record<string, unknown>
): void {
  const key = `form:${formId}:${action}`
  if (action === 'focus' && dedupe(key)) return

  const actionLabel = action === 'submit' ? `Form submitted: ${formId}` : `Form ${action}: ${formId}`
  const payload = mergePayload({
    action_type: 'form',
    action_name: actionLabel,
    form_id: formId,
    form_action: action,
    event_action: 'form',
    event_label: actionLabel,
    ...extra,
  })

  trackEventUtils('Action: Form', payload)
  addBreadcrumb({
    category: 'form',
    message: `${actionLabel}${payload.page_name ? ` (on ${payload.page_name})` : ''}`,
    level: 'info',
    data: { form_id: formId, action, page_id: payload.page_id, page_name: payload.page_name },
  })
}

// ─── Modal ───────────────────────────────────────────────────────────────────

/**
 * Modal open/close — "Action: Modal" with page context so you see "Opened Add Provider on Providers".
 */
export function trackModalOpen(modalName: string, extra?: Record<string, unknown>): void {
  const payload = mergePayload({
    action_type: 'modal',
    action_name: `Modal opened: ${modalName}`,
    modal_name: modalName,
    modal_action: 'open',
    event_action: 'modal_open',
    event_label: `Modal opened: ${modalName}`,
    ...extra,
  })
  trackEventUtils('Action: Modal Open', payload)
  addBreadcrumb({
    category: 'ui',
    message: `Modal opened: ${modalName}${payload.page_name ? ` (on ${payload.page_name})` : ''}`,
    level: 'info',
    data: { modal_name: modalName, page_id: payload.page_id, page_name: payload.page_name },
  })
}

export function trackModalClose(modalName: string, extra?: Record<string, unknown>): void {
  const payload = mergePayload({
    action_type: 'modal',
    action_name: `Modal closed: ${modalName}`,
    modal_name: modalName,
    modal_action: 'close',
    event_action: 'modal_close',
    event_label: `Modal closed: ${modalName}`,
    ...extra,
  })
  trackEventUtils('Action: Modal Close', payload)
  addBreadcrumb({
    category: 'ui',
    message: `Modal closed: ${modalName}${payload.page_name ? ` (on ${payload.page_name})` : ''}`,
    level: 'info',
    data: { modal_name: modalName, page_id: payload.page_id, page_name: payload.page_name },
  })
}
